import type { SwChatClient } from '@swchat/client-sdk';
import { Component, useEffect, useState, type ReactNode } from 'react';
import { getClient } from './lib/client';
import { ClientContext, useClient, useStatus } from './lib/hooks';
import { CALLBACK_PATH, redirectUri, takeCallback } from './lib/pkce';
import { takeConversationParam } from './lib/pwa';
import { isStaleChunkError, reloadForNewRelease } from './lib/release';
import { Login, Recovery, RecoveryKeyNotice, Revoked, SetupIdentity, WaitingForPortal } from './screens/Onboarding';
import { Workspace } from './workspace/Workspace';

export interface AppProps {
  /** Chế độ nhúng trong 7w_portal: đăng nhập bằng token portal qua postMessage. */
  embed?: {
    portalToken?: string;
    conversationId?: string;
    onUnread?: (count: number) => void;
    /** W2.5: nhờ portal mở URL ngoài (`open_external`) — iframe không tự mở tab mới. */
    onOpenExternal?: (url: string) => void;
  };
}

export function App({ embed }: AppProps) {
  const [client, setClient] = useState<SwChatClient>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    getClient().then(setClient, (e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return <main className="center error" data-testid="app-error">Không khởi động được: {error}</main>;
  if (!client) return <main className="center muted" data-testid="app-loading">Đang tải…</main>;
  return (
    <ClientContext.Provider value={client}>
      <ErrorBoundary>
        <Shell embed={embed} />
      </ErrorBoundary>
    </ClientContext.Provider>
  );
}

/** Q4: `?c=<id>` — service worker mở cửa sổ từ thông báo đẩy → mở thẳng hội thoại (web chính). */
const conversationFromUrl = window.location.pathname.startsWith('/embed') ? undefined : takeConversationParam();

/** Code trong URL callback chỉ dùng được một lần (StrictMode chạy effect hai lần ở dev). */
let callbackHandled = false;

function Shell({ embed }: AppProps) {
  const client = useClient();
  const status = useStatus();
  const [loginError, setLoginError] = useState<string>();
  /** Recovery Key vừa tạo, giữ màn hình cho tới khi người dùng xác nhận đã lưu. */
  const [recoveryKey, setRecoveryKey] = useState<string>();
  /** Đang tạo danh tính: client chuyển sang 'ready' trước khi trả Recovery Key — giữ màn hình thiết lập. */
  const [creating, setCreating] = useState(false);

  // Web riêng: quay về từ portal với ?code=…
  useEffect(() => {
    if (embed || callbackHandled || window.location.pathname !== CALLBACK_PATH) return;
    callbackHandled = true;
    const cb = takeCallback();
    if (!cb) return setLoginError('Phiên đăng nhập không hợp lệ, hãy thử lại.');
    client.loginWithCode(cb.code, cb.verifier, redirectUri()).catch((e) => setLoginError(e instanceof Error ? e.message : String(e)));
  }, [client, embed]);

  // Nhúng: mỗi token portal mới → đổi lấy phiên (đổi người dùng thì SDK xóa dữ liệu của người trước).
  useEffect(() => {
    if (!embed?.portalToken) return;
    client.loginWithPortalToken(embed.portalToken).catch((e) => setLoginError(e instanceof Error ? e.message : String(e)));
  }, [client, embed?.portalToken]);

  if (recoveryKey) return <RecoveryKeyNotice recoveryKey={recoveryKey} onDone={() => setRecoveryKey(undefined)} />;
  if (creating) return <SetupIdentity busy />;

  switch (status) {
    case 'signed_out':
      return embed ? <WaitingForPortal /> : <Login error={loginError} />;
    case 'needs_identity':
      return (
        <SetupIdentity
          onStart={() => setCreating(true)}
          onCreated={(rk) => (setRecoveryKey(rk), setCreating(false))}
          onFailed={() => setCreating(false)}
        />
      );
    case 'needs_recovery':
      return <Recovery />;
    case 'revoked':
      return <Revoked />;
    case 'ready':
      return (
        <Workspace
          initialConversation={embed ? embed.conversationId : conversationFromUrl}
          onUnread={embed?.onUnread}
          onOpenExternal={embed?.onOpenExternal}
          embed={!!embed}
        />
      );
  }
}

/** Lỗi giao diện không làm mất cả trang; dữ liệu và khóa vẫn nằm trong IndexedDB. */
class ErrorBoundary extends Component<{ children: ReactNode }, { error?: Error }> {
  override state: { error?: Error } = {};

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error) {
    if (isStaleChunkError(error)) reloadForNewRelease();
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="center">
        <section className="card narrow">
          <h2>Đã xảy ra lỗi giao diện</h2>
          <p className="error">{this.state.error.message}</p>
          <button className="primary" onClick={() => window.location.reload()}>
            Tải lại
          </button>
        </section>
      </main>
    );
  }
}
